<script setup lang="ts">
// Slides web: https://antoniandre.github.io/vueper-slides/#ex--show-multiple-slides-and-gap
import { VueperSlides, VueperSlide } from 'vueperslides'
import { useScreen } from '@/composables/useScreen'
import { computed } from 'vue'

const { recommendedEntitiesList } = defineProps({
  recommendedEntitiesList: {
    type: Array,
    required: true,
    default: () => []
  }
})

const { isMobileView, screenWidth } = useScreen()
const visibleSlidesCount = computed(() => {
  if (isMobileView.value || screenWidth.value < 1360) {
    return 1
  }

  return 2
})
const arrowShift = computed(() => 45 + (recommendedEntitiesList.length * (isMobileView.value? 34 : 44) / 2))
const isAutoplayActive = computed(() => recommendedEntitiesList.length / visibleSlidesCount.value > 1)
</script>
<template>
  <div>
    <div v-if="isMobileView">
      <vueper-slides
        class="no-shadow"
        fixed-height="710px"
        :arrows="true"
        :gap="3"
        bullets-outside
        :autoplay="isAutoplayActive"
        :pauseOnHover="false"
        :pauseOnTouch="true"
        :preventYScroll="true"
        :dragging-distance="70"
      >
        <template #bullet="{ active }">
          <div class="relative w-[25px] h-[4px] -mx-[6px] top-[70px] bg-darkBlue" :class="{'opacity-55' : !active}" />
        </template>
        <template #arrow-left>
          <div class="relative top-[399px]" :style="{left: `-${arrowShift}px`}">
            <IconArrowFullLeft class="text-darkBlue" />
          </div>
        </template>
        <template #arrow-right>
          <div class="relative top-[399px]" :style="{right: `-${arrowShift}px`}">
            <IconArrowFullRight class="text-darkBlue" />
          </div>
        </template>
        <vueper-slide v-for="(entity, index) in recommendedEntitiesList" :key="index">
          <template #content>
            <div class="flex items-center justify-center">
              <div class="flex items-center justify-center z-10 text-darkBlue p-3 font-redhat font-medium text-sm bg-white-300 fixed top-8 w-[98px] h-[52px] rounded-r-xl mr-[239px]">
                <slot name="badge" :status="entity.lawyer_profession_status" />
              </div>
            </div>
            <div class="flex items-center justify-center">
              <el-avatar :src="entity.picture" class="!bg-white-500 !w-[327px] !h-[374px] !rounded-[40px]">
                <slot name="picture" />
              </el-avatar>
            </div>
            <div class="flex items-center justify-center">
              <div class="font-redhat font-medium text-white w-[295px] h-[346px] bg-lightBlue rounded-[40px] relative bottom-6">
                <div class="ml-6 mr-6 pt-6 text-2xlg">
                  <nuxt-link :to="entity.url">
                    {{ entity.title }}
                  </nuxt-link>
                </div>
                <div class="ml-6 mr-6 mt-2 text-sm">
                  <div v-if="entity.experience">
                    {{ entity.experience }} {{ $t('law_practice') }}
                  </div>
                </div>
                <div class="ml-6 mt-2">
                  <rating-stars :value="entity.reviewsRating" :text-size="16" />
                </div>
                <div class="ml-6 mr-6 mt-4 leading-normal text-sm truncate">
                  {{ entity.about }}
                </div>
                <div class="absolute bottom-[60px] w-[60px] border-b-2 border-white-500 ml-6" />
                <nuxt-link :to="entity.url" class="ml-6 absolute bottom-[21px] text-18">
                  {{ $t('more') }}
                </nuxt-link>
                <div class="absolute bottom-[20px] right-[16px]">
                  <nuxt-link :to="entity.url">
                    <IconArrowRightCircle class="size-8 text-white" />
                  </nuxt-link>
                </div>
              </div>
            </div>
          </template>
        </vueper-slide>
      </vueper-slides>
    </div>
    <div v-else>
      <vueper-slides
        class="no-shadow"
        :visible-slides="visibleSlidesCount"
        :gap="0"
        fixed-height="510px"
        arrows-outside
        bullets-outside
        :autoplay="isAutoplayActive"
        :touchable="false"
      >
        <template #bullet="{ active }">
          <div class="relative mb-[18px] w-[35px] h-[4px] -mx-[6px] top-7 bg-darkBlue" :class="{'opacity-55' : !active}" />
        </template>
        <template #arrow-left>
          <div class="relative top-[239px]" :style="{left: `-${arrowShift}px`}">
            <IconArrowFullLeft class="text-darkBlue" />
          </div>
        </template>
        <template #arrow-right>
          <div class="relative top-[239px]" :style="{right: `-${arrowShift}px`}">
            <IconArrowFullRight class="text-darkBlue" />
          </div>
        </template>
        <vueper-slide v-for="(entity, index) in recommendedEntitiesList" :key="index">
          <template #content>
            <div class="flex text-white" :class="{'justify-center items-center': visibleSlidesCount == 1}">
              <div class="relative">
                <div class="bg-white-300 rounded-r-xl w-[98px] h-[52px] absolute z-10 text-darkBlue flex items-center justify-center p-3 font-redhat font-medium text-sm" :class="visibleSlidesCount > 1? 'top-[80px]' : 'bottom-[80px]'">
                  <slot name="badge" :status="entity.lawyer_profession_status" />
                </div>
              </div>
              <el-avatar :src="entity.picture" class="!bg-white-500 !w-[327px] !h-[430px] !rounded-[40px]">
                <slot name="picture" />
              </el-avatar>
              <div class="font-redhat font-medium w-[362px] h-[386px] bg-lightBlue rounded-[40px]" :class="visibleSlidesCount > 1? 'top-[22px] absolute right-[10px]' : 'relative right-[60px]'">
                <div class="ml-6 mr-6 pt-8 text-30 hover:underline">
                  <nuxt-link :to="entity.url">
                    {{ entity.title }}
                  </nuxt-link>
                </div>
                <div class="ml-6 mr-6 mt-2">
                  <div v-if="entity.experience">
                    {{ entity.experience }} {{ $t('law_practice') }}
                  </div>
                </div>
                <div v-if="entity.reviewsRating" class="ml-6 mt-2">
                  <rating-stars :value="entity.reviewsRating" />
                </div>
                <div class="ml-6 mr-6 mt-6 leading-normal text-md font-redhat truncate">
                  {{ entity.about }}
                </div>
                <div class="absolute bottom-[86px] w-[65px] border-b-2 border-white-500 ml-6" />
                <nuxt-link :to="entity.url" class="ml-6 absolute bottom-[36px] text-18">
                  {{ $t('more') }}
                </nuxt-link>
                <div class="absolute bottom-[34px] right-[30px]">
                  <nuxt-link :to="entity.url">
                    <IconArrowRightCircle class="size-8 text-white" />
                  </nuxt-link>
                </div>
              </div>
            </div>
          </template>
        </vueper-slide>
      </vueper-slides>
    </div>
  </div>
</template>
<style scoped>
:deep(.vueperslides__bullet)s{
  position: absolute;
}

:deep(.vueperslides__arrow) {
  color: #0E1535;
}

:deep(.vueperslides__arrow) {
  opacity: 1;
}

:deep(.vueperslides__arrow--prev) {
  left: 50%;
}

:deep(.vueperslides__arrow--next) {
  right: 50%;
}
</style>